import React, { useState } from "react";
import { Toaster as SonnerToaster, toast as sonnerToast, useSonner } from "sonner";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faSync,
  faCheck,
  faChevronUp,
  faChevronDown,
} from "@fortawesome/free-solid-svg-icons";
import theme from "../../../stylesheets/theme";
import { darken } from "polished";

// PLAT-3416 - the house toaster, on Sonner (Roy, 2026-08-31 GMT+8).
//
// A SHARED COMPONENT. The provider is mounted once at pages/Inbox/Inbox.js, and
// `components/IntegrationModal` forwards toast options VERBATIM from a
// third-party extension iframe - so any option this module honours is reachable
// by an extension.
//
// TWO THINGS SONNER DOES NOT PROVIDE, and are therefore custom below:
//   - ANY per-toast aria. It hard-codes one `aria-live="polite"` container and
//     puts no `role` on toasts; `ToastT` has no `ariaProps`. See AssertiveMessage.
//   - a collapse affordance that says HOW MANY are hidden. It hides the overflow
//     silently.
//
// Sonner injects its own stylesheet from JS at import time, so there is no CSS
// import and no Tailwind here.

const SPACING = 8;

const TEXT_COLOR = "#FFFFFF";

const ERROR_ICON_COLOR = theme.dangerText
const SUCCESS_ICON_COLOR = theme.success
const LOADING_ICON_COLOR = "#eab308"

const FONT_SIZE = "14px";

// "80% translucent" is ambiguous; this is 80% OPAQUE. Alpha 0.2 was rendered
// over the real conversation pane and white text on it is not legible.
const BACKDROP_ALPHA = 0.8;
const BACKDROP = `rgba(0, 0, 0, ${BACKDROP_ALPHA})`;
const BACKDROP_BORDER = "rgba(255, 255, 255, 0.14)";
const BACKDROP_SHADOW = "0 4px 16px rgba(0, 0, 0, 0.28)";

// Three visible, the rest behind a counted affordance. Also Sonner's default,
// so the number is not being fought.
const MAX_VISIBLE = 3;

// Wider than Sonner's 356px: the MoveThreads body puts a label and an action
// button on one row and was truncating itself to fit.
const TOAST_WIDTH = "420px";

// The house auto-dismiss. Sonner's own default is 4000ms (TOAST_LIFETIME);
// react-hot-toast's provider here set 8000. Kept at 8000 so no existing
// consumer's toast gets shorter than it was.
const DEFAULT_DURATION = 8000;

const EDGE_OFFSET = 16;
// Vertical room reserved for the collapse affordance, so it sits BELOW the
// stack instead of underneath the front toast.
const AFFORDANCE_RESERVE = 42;

const ErrorIcon = styled(FontAwesomeIcon)`
  color: ${ERROR_ICON_COLOR};
`;

const SuccessIcon = styled(FontAwesomeIcon)`
  color: ${SUCCESS_ICON_COLOR};
`;

const LoadingIcon = styled(FontAwesomeIcon)`
  color: ${LOADING_ICON_COLOR};
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const BUTTON_TEXT_COLOR = "#3B82F6";
const BUTTON_HOVER_BACKGROUND = "rgba(59, 130, 246, 0.1)";

const ToastButton = styled.button`
  padding-bottom: 0;
  background: transparent;
  color: ${BUTTON_TEXT_COLOR};
  border: none;
  border-radius: 4px;
  font-size: ${FONT_SIZE};
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${BUTTON_HOVER_BACKGROUND};
  }

  &:active {
    background-color: ${darken(0.1, BUTTON_HOVER_BACKGROUND)};
  }
`;

/**
 * An error MUST interrupt, and Sonner cannot do it: its one container is
 * `aria-live="polite"` with the politeness hard-coded, and its toasts carry no
 * `role`. So the live region is nested inside the message - `role="alert"` is
 * implicitly assertive and is the nearest live-region ancestor of its own text.
 *
 * KNOWN LIMITATION: the container's `aria-live="polite"` remains and is not
 * configurable. Changing it would make every success toast interrupt too.
 */
const AssertiveMessage = ({ children }) => (
  <span role="alert" aria-live="assertive">{children}</span>
);

// Marks a toast as carrying a close control, so ToasterShell can reserve room
// for it. Defined here, next to the only thing that applies it.
const PERMANENT_TOAST_CLASS = "woz-toast-permanent";

/**
 * Per-toast opt-in to PERMANENT mode: `toast.error(message, permanent())`.
 *
 * A helper rather than a flag because the mode needs TWO settings that MUST NOT
 * disagree - `duration: Infinity` and `closeButton: true`. One without the other
 * gives a toast that vanishes while showing a close button, or one with no way
 * to dismiss it.
 *
 * DO NOT PASS A STABLE `id` unless exactly one such toast should ever exist:
 * Sonner treats a repeated id as an UPDATE, so a shared id silently collapses
 * every later failure into the first and the user never learns it recurred.
 */
function permanent(options = {}) {
  return {
    ...options,
    duration: Infinity,
    closeButton: true,
    // Tags the toast so the shell reserves room for the close control without
    // `:has()` and without padding toasts that have no button.
    className: [options.className, PERMANENT_TOAST_CLASS].filter(Boolean).join(" "),
  };
}

// The house `toast`. Sonner builds its export with `Object.assign`, so every
// method is an own enumerable property and copies cleanly. The ONLY behaviour
// added is the assertive live region on errors.
const toast = Object.assign(
  (message, options) => sonnerToast(message, options),
  sonnerToast,
  {
    error: (message, options) =>
      sonnerToast.error(<AssertiveMessage>{message}</AssertiveMessage>, options),
  },
);

const CollapseToggle = styled.button`
  position: fixed;
  right: ${EDGE_OFFSET}px;
  bottom: ${EDGE_OFFSET}px;
  /* Sonner's own list sits at 999999999; stay on the same layer as the stack
     it belongs to, one below so it can never cover the front toast. */
  z-index: 999999998;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border: 1px solid ${BACKDROP_BORDER};
  border-radius: 999px;
  background: ${BACKDROP};
  box-shadow: ${BACKDROP_SHADOW};
  color: ${TEXT_COLOR};
  font-size: 12px;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover { background: rgba(0, 0, 0, ${Math.min(BACKDROP_ALPHA + 0.12, 1)}); }
  &:focus-visible { outline: 2px solid #ffffff; outline-offset: 2px; }
`;

// CLOSE CONTROL GEOMETRY. Named because the padding below is DERIVED from
// them: change a number here and the room reserved for the button follows.
const CLOSE_SIZE = 24; // hit area, not icon size - the glyph stays 12px
const CLOSE_INSET = 8; // from the trailing edge, INSIDE the pill
const CLOSE_TEXT_GAP = 12; // clear space between message and button

/**
 * The close control is the only way out of a permanent toast, so it sits INSIDE
 * the pill. Sonner's default pushes it outside with `translate(-35%, -35%)`,
 * which its own skin hides behind an opaque badge and ours does not.
 *
 * Centred, not top-pinned: these carry verbatim Meta errors, which wrap.
 *
 * LOGICAL PROPERTIES, not `right`/`padding-right` - the Sonner variables this
 * replaces are dir-aware, and physical sides would silently drop RTL mirroring.
 */
const ToasterShell = styled.div`
  [data-sonner-toast] [data-close-button] {
    inset-inline-end: ${CLOSE_INSET}px;
    inset-inline-start: unset;
    top: 50%;
    transform: translateY(-50%);
    width: ${CLOSE_SIZE}px;
    height: ${CLOSE_SIZE}px;
    border-radius: 4px;
  }

  /* COLOUR IS LEFT TO SONNER ON PURPOSE. Its dark-theme rule is 0,4,0 and
     outranks this shell's 0,3,0, so any background declared here is silently
     ignored. Its result - an always-visible bordered control - is what a
     permanent toast needs anyway. */

  /* Focus ring: outline is set by no Sonner rule, so this one does apply.
     NO BACKTICKS IN A CSS COMMENT - it is inside a template literal. */
  [data-sonner-toast] [data-close-button]:focus-visible {
    outline: 2px solid #ffffff;
    outline-offset: 1px;
  }

  /* RESERVE THE ROOM. Without this the message runs under the button at the
     trailing end - the toast is a fixed --width, so a long line has nowhere
     else to go. Derived from the geometry above so the two cannot drift. */
  [data-sonner-toast][data-styled="true"].${PERMANENT_TOAST_CLASS} {
    padding-inline-end: ${CLOSE_INSET + CLOSE_SIZE + CLOSE_TEXT_GAP}px;
  }
`;

function Toaster() {
  // Bound to the BACKLOG it was opened for, not a plain boolean: a boolean stays
  // true after the first expand, so a later burst renders every toast and the
  // "3 visible" invariant is gone for the rest of the session.
  const [expandedFor, setExpandedFor] = useState(null);

  const { toasts } = useSonner();
  // NEWEST FIRST - `useSonner` prepends. Sonner's two accessors DISAGREE: the
  // array behind `toast.getToasts()` is ordered newest LAST. Only adding a toast
  // to an EXISTING backlog tells them apart, so this is easy to get wrong and
  // hard to catch.
  const newestId = toasts.length ? toasts[0].id : null;
  const expanded = expandedFor !== null && expandedFor === newestId;

  const hiddenCount = expanded ? 0 : Math.max(0, toasts.length - MAX_VISIBLE);
  const showCollapseBack = expanded && toasts.length > MAX_VISIBLE;
  const affordanceVisible = hiddenCount > 0 || showCollapseBack;

  return (
    <>
      <ToasterShell>
        <SonnerToaster
          position="bottom-right"
          // Sonner's collapsed default renders ONE readable toast with the rest
          // as blank cards behind it; `expand` is what makes three readable.
          expand
          visibleToasts={expanded ? Math.max(toasts.length, MAX_VISIBLE) : MAX_VISIBLE}
          duration={DEFAULT_DURATION}
          gap={SPACING}
          theme="dark"
          offset={{
            right: EDGE_OFFSET,
            bottom: affordanceVisible ? EDGE_OFFSET + AFFORDANCE_RESERVE : EDGE_OFFSET,
          }}
          icons={{
            error: <ErrorIcon icon={faExclamationTriangle} color={TEXT_COLOR} />,
            success: <SuccessIcon icon={faCheck} color={TEXT_COLOR} />,
            loading: <LoadingIcon icon={faSync} color={TEXT_COLOR} />,
          }}
          // CSS CUSTOM PROPERTIES ARE THE ONLY OVERRIDE POINT THAT WORKS:
          // Sonner's rules are keyed on `[data-sonner-toast][data-styled='true']`,
          // which outranks any single class passed through `classNames`.
          style={{
            "--width": TOAST_WIDTH,
            "--normal-bg": BACKDROP,
            "--normal-border": BACKDROP_BORDER,
            "--normal-text": TEXT_COLOR,
            "--border-radius": "6px",
            // No close-button variables here: Sonner hard-codes the button's
            // `top` with no variable for it, so placement has to be a rule.
            // See ToasterShell.
          }}
          toastOptions={{
            // MUST be on toastOptions, not on <Toaster> - Sonner ignores it
            // there silently and keeps its default "Close toast".
            closeButtonAriaLabel: "Dismiss notification",
            style: {
              fontSize: FONT_SIZE,
              boxShadow: BACKDROP_SHADOW,
              // Outer bound for a long verbatim Meta body; Sonner already wraps.
              maxHeight: "50vh",
              overflowY: "auto",
            },
          }}
        />
      </ToasterShell>

      {hiddenCount > 0 ? (
        <CollapseToggle
          type="button"
          onClick={() => setExpandedFor(newestId)}
          aria-expanded={false}
          aria-label={`Show ${hiddenCount} more notification${hiddenCount === 1 ? "" : "s"}`}
        >
          <FontAwesomeIcon icon={faChevronUp} />
          {`${hiddenCount} more notification${hiddenCount === 1 ? "" : "s"}`}
        </CollapseToggle>
      ) : null}

      {showCollapseBack ? (
        <CollapseToggle
          type="button"
          onClick={() => setExpandedFor(null)}
          aria-expanded={true}
          aria-label={`Collapse to ${MAX_VISIBLE} notifications`}
        >
          <FontAwesomeIcon icon={faChevronDown} />
          Show fewer
        </CollapseToggle>
      ) : null}
    </>
  );
}

const TOAST_HEIGHT = "40px"

// The custom-body container MoveThreads and ManageThreadsPopper build inside.
//
// Sonner renders a `toast.custom` body UNSTYLED, so this component owns its
// whole appearance - no padding, background, border or width comes from Sonner.
const CustomToastContainer = styled.div`
  display: flex;
  position: relative;
  gap: ${SPACING}px;
  justify-content: center;
  align-items: center;
  min-height: ${TOAST_HEIGHT};
  background: ${BACKDROP};
  border: 1px solid ${BACKDROP_BORDER};
  box-shadow: ${BACKDROP_SHADOW};
  color: ${TEXT_COLOR};
  padding: ${SPACING/2}px ${SPACING * 2}px;
  border-radius: 6px;
  opacity: 0;
  animation: fadeInFromTop 0.5s forwards;
  visibility: ${p => p.hideToast ? "hidden": "visible"};
  @keyframes fadeInFromTop {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

export {
  Toaster,
  toast,
  permanent,

  CustomToastContainer as ToastContainer,
  SuccessIcon as ToastSuccessIcon,
  ErrorIcon as ToastErrorIcon,
  LoadingIcon as ToastLoadingIcon,
  ToastButton,
};
